/**
 * KTX2 Texture Loader Utility
 *
 * Provides optimized texture loading using KTX2/Basis compressed textures
 * for faster loading times and reduced memory footprint.
 *
 * Benefits:
 * - GPU-native compression formats (ETC1S, UASTC)
 * - Smaller file sizes (typically 50-80% reduction)
 * - Faster loading and parsing
 * - Lower memory usage on GPU
 * - Automatic format selection based on device capabilities
 *
 * @example
 * import { useKTX2Texture } from './utils/KTX2TextureLoader';
 *
 * function MyComponent() {
 *   const textures = useKTX2Texture({
 *     albedo: '/textures/wood_albedo.ktx2',
 *     normal: '/textures/wood_normal.ktx2',
 *   });
 *
 *   return <mesh><meshStandardMaterial map={textures.albedo} normalMap={textures.normal} /></mesh>
 * }
 */

import { useEffect, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import * as THREE from 'three';

// Singleton loader instance
let ktx2Loader = null;

// Global texture cache to prevent re-loading
const textureCache = new Map();

// Cache for in-flight loading promises to prevent race conditions
const loadingPromises = new Map();

/**
 * Clear the texture cache (useful for cleanup)
 */
export function clearTextureCache() {
  textureCache.forEach(texture => {
    if (texture && texture.dispose) {
      texture.dispose();
    }
  });
  textureCache.clear();
  loadingPromises.clear();
}

/**
 * Initialize the KTX2 loader with WebGL renderer
 * @param {THREE.WebGLRenderer} renderer - The Three.js renderer
 * @returns {KTX2Loader} Configured KTX2 loader instance
 */
export function getKTX2Loader(renderer) {
  if (!ktx2Loader) {
    ktx2Loader = new KTX2Loader();

    // Set the path to the basis transcoder files
    // These files are included in three.js and need to be copied to your public folder
    ktx2Loader.setTranscoderPath('/basis/');

    // Detect WebGL2 renderer capabilities
    ktx2Loader.detectSupport(renderer);

    console.log('✅ KTX2Loader initialized with Basis Universal transcoder');
  }

  return ktx2Loader;
}

/**
 * Load a single KTX2 texture with caching
 * @param {string} url - Path to the KTX2 texture file
 * @param {THREE.WebGLRenderer} renderer - The Three.js renderer
 * @param {string} textureType - Type of texture (albedo, normal, etc.) for correct color space
 * @returns {Promise<THREE.Texture>} Promise that resolves to the loaded texture
 */
export function loadKTX2Texture(url, renderer, textureType = 'albedo') {
  const cacheKey = `${url}|${textureType}`;

  // Check if texture is already loaded in cache
  if (textureCache.has(cacheKey)) {
    console.log(`♻️  Using cached KTX2 texture: ${url}`);
    return Promise.resolve(textureCache.get(cacheKey));
  }

  // Check if texture is currently being loaded (prevent race condition)
  if (loadingPromises.has(cacheKey)) {
    console.log(`⏳ Waiting for in-flight KTX2 load: ${url}`);
    return loadingPromises.get(cacheKey);
  }

  // Create new loading promise
  const loader = getKTX2Loader(renderer);

  const loadingPromise = new Promise((resolve, reject) => {
    console.log(`🔄 Starting KTX2 load: ${url}`);
    loader.load(
      url,
      (texture) => {
        // Configure texture settings
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;

        // Enable anisotropic filtering for sharp textures at oblique angles
        const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
        texture.anisotropy = maxAnisotropy;

        // Set correct color space based on texture type
        // Albedo/color maps should be in sRGB, others should be linear
        if (textureType === 'albedo' || textureType === 'map') {
          texture.colorSpace = THREE.SRGBColorSpace;
        } else {
          texture.colorSpace = THREE.LinearSRGBColorSpace;
        }

        texture.needsUpdate = true;

        // Store in cache
        textureCache.set(cacheKey, texture);

        // Remove from loading promises
        loadingPromises.delete(cacheKey);

        console.log(`✅ Loaded KTX2 texture: ${url} (${textureType}, colorSpace: ${texture.colorSpace})`);
        resolve(texture);
      },
      undefined, // onProgress callback
      (error) => {
        // Remove from loading promises on error
        loadingPromises.delete(cacheKey);
        console.error(`❌ Failed to load KTX2 texture: ${url}`, error);
        reject(error);
      }
    );
  });

  // Store the loading promise to prevent duplicate loads
  loadingPromises.set(cacheKey, loadingPromise);

  return loadingPromise;
}

/**
 * Load multiple KTX2 textures
 * @param {Object} textureMap - Object mapping texture names to URLs
 * @param {THREE.WebGLRenderer} renderer - The Three.js renderer
 * @returns {Promise<Object>} Promise that resolves to object with loaded textures
 *
 * @example
 * const textures = await loadKTX2Textures({
 *   albedo: '/textures/wood_albedo.ktx2',
 *   normal: '/textures/wood_normal.ktx2',
 * }, renderer);
 */
export async function loadKTX2Textures(textureMap, renderer) {
  const entries = Object.entries(textureMap);
  const promises = entries.map(([name, url]) =>
    loadKTX2Texture(url, renderer).then(texture => [name, texture])
  );

  const results = await Promise.all(promises);
  return Object.fromEntries(results);
}

/**
 * React hook for loading KTX2 textures
 * @param {Object|string} textureUrls - Single URL or object mapping names to URLs
 * @param {Object} options - Loading options
 * @param {Function} options.onProgress - Progress callback
 * @returns {Object|THREE.Texture|null} Loaded texture(s) or null if still loading
 *
 * @example
 * // Load single texture
 * const texture = useKTX2Texture('/textures/wood.ktx2');
 *
 * // Load multiple textures
 * const textures = useKTX2Texture({
 *   albedo: '/textures/wood_albedo.ktx2',
 *   normal: '/textures/wood_normal.ktx2',
 * });
 */
export function useKTX2Texture(textureUrls, options = {}) {
  const { gl } = useThree();
  const [textures, setTextures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadTextures = async () => {
      try {
        setLoading(true);
        setError(null);

        let result;

        if (typeof textureUrls === 'string') {
          // Single texture
          result = await loadKTX2Texture(textureUrls, gl);
        } else {
          // Multiple textures
          result = await loadKTX2Textures(textureUrls, gl);
        }

        if (isMounted) {
          setTextures(result);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
          setLoading(false);
          console.error('Failed to load KTX2 textures:', err);
        }
      }
    };

    loadTextures();

    return () => {
      isMounted = false;

      // Dispose textures on unmount
      if (textures) {
        if (textures.isTexture) {
          textures.dispose();
        } else {
          Object.values(textures).forEach(texture => {
            if (texture?.dispose) texture.dispose();
          });
        }
      }
    };
  }, [gl, JSON.stringify(textureUrls)]);

  return { textures, loading, error };
}

/**
 * Load material textures with fallback to PNG/JPG
 * @param {Object} materialConfig - Material configuration object
 * @param {THREE.WebGLRenderer} renderer - The Three.js renderer
 * @param {boolean} preferKTX2 - Whether to prefer KTX2 over PNG/JPG (default: true)
 * @returns {Promise<Object>} Loaded textures
 *
 * @example
 * const textures = await loadMaterialTextures(materialConfig, renderer);
 */
export async function loadMaterialTextures(materialConfig, renderer, preferKTX2 = true) {
  const { basePath, textures: textureFiles, name } = materialConfig;
  const textureMap = {};
  let usedFallback = false;
  const fallbackTextures = [];

  for (const [textureType, filename] of Object.entries(textureFiles)) {
    if (!filename) continue;

    const originalPath = `${basePath}/${filename}`;

    // Try KTX2 first if preferred
    if (preferKTX2) {
      const ktx2Path = originalPath.replace(/\.(png|jpg|jpeg)$/i, '.ktx2');

      try {
        // Check if KTX2 file exists by attempting to load it
        console.log(`🔍 Attempting to load KTX2: ${ktx2Path}`);
        const texture = await loadKTX2Texture(ktx2Path, renderer, textureType);
        textureMap[textureType] = texture;
        console.log(`✅ Using KTX2 for ${name} - ${textureType}`);
        continue;
      } catch (err) {
        // KTX2 not found, will fall back to PNG/JPG
        usedFallback = true;
        fallbackTextures.push(textureType);
        console.error(`⚠️  KTX2 failed for ${textureType} (${ktx2Path}):`, err);
      }
    }

    // Fallback to original PNG/JPG (with caching)
    const fallbackCacheKey = `${originalPath}|${textureType}`;
    if (textureCache.has(fallbackCacheKey)) {
      console.log(`♻️  Using cached PNG/JPG texture: ${originalPath}`);
      textureMap[textureType] = textureCache.get(fallbackCacheKey);
    } else {
      textureMap[textureType] = await new Promise((resolve, reject) => {
        new THREE.TextureLoader().load(
          originalPath,
          (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;

            // Enable anisotropic filtering for sharp textures at oblique angles
            const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
            texture.anisotropy = maxAnisotropy;

            // Set correct color space for PNG/JPG too
            if (textureType === 'albedo') {
              texture.colorSpace = THREE.SRGBColorSpace;
            }

            // Store in cache
            textureCache.set(fallbackCacheKey, texture);

            resolve(texture);
          },
          undefined,
          reject
        );
      });
    }
  }

  // Warning if fallback was used
  if (usedFallback && preferKTX2) {
    console.warn(
      `⚠️  PNG/JPG fallback gebruikt voor "${name}"!\n` +
      `   Texturen zonder KTX2: ${fallbackTextures.join(', ')}\n` +
      `   💡 Converteer naar KTX2 voor betere performance:\n` +
      `      ./scripts/convert_to_ktx2.sh ${basePath}`
    );
  }

  return textureMap;
}

/**
 * Dispose of the KTX2 loader (cleanup)
 */
export function disposeKTX2Loader() {
  if (ktx2Loader) {
    ktx2Loader.dispose();
    ktx2Loader = null;
    console.log('🧹 KTX2Loader disposed');
  }
}
